import { useCallback, useEffect, useRef, useState } from 'react';
import { PTZRegion } from './PTZRegion';
import { useSelector } from 'react-redux';
import { selectIsWindowDisconnectedFromSharedWorker } from 'store/generalSlice/generalSelectors';

export default function usePTZRegion(cameraConfig, isCameraFocused = false, initialIsPTZOn = false, onPTZOnChange) {
    const canvasRef = useRef();
    const ptzRegion = useRef();
    const [isPTZOn, setIsPTZOn] = useState(initialIsPTZOn);
    const [ptzRequestStatus, setPTZRequestStatus] = useState({ status: null, message: '', tooltipMessage: '' });
    const [canvasVisible, setCanvasVisible] = useState(false);
    const isWindowDisconnectedFromSharedWorker = useSelector(selectIsWindowDisconnectedFromSharedWorker);

    const keyDownListener = useCallback(
        (e) => {
            const key = e.key;
            if (!isCameraFocused || isNaN(key)) return;
            if (key === 'i' || key === 'I') {
                ptzRegion.current?.fullZoomIn();
                return;
            }
            if (key === 'o' || key === 'O') {
                ptzRegion.current?.fullZoomOut();
                return;
            }
            ptzRegion.current?.goToPreset(key);
        },
        [isCameraFocused, ptzRegion]
    );

    const startNewRegion = useCallback(() => {
        window.addEventListener('keydown', keyDownListener);
        if (ptzRegion.current) {
            ptzRegion.current.start();
            setCanvasVisible(true);
        }
    }, [keyDownListener]);

    const togglePTZActive = useCallback(() => {
        setIsPTZOn((prev) => {
            if (prev) {
                ptzRegion.current?.close?.();
            }
            return !prev;
        });
    }, []);

    const togglePTZCameraEnlarged = useCallback(
        (isExpanded) => {
            if (isExpanded) {
                isPTZOn && startNewRegion();
                setIsPTZOn(true);
            } else {
                setIsPTZOn(false);
                ptzRegion.current?.close?.();
            }
        },
        [isPTZOn, startNewRegion]
    );

    const { type, camId } = cameraConfig || {};
    const isPTZCamera = type === 'PTZ';

    const canvasInlineStyles = {
        position: 'absolute',
        top: 0,
        left: 0,
        visibility: canvasVisible ? 'visible' : 'hidden',
    };

    const initializeNewPTZRegion = useCallback(
        (videoElement, divRef, digitalZoomRef) => {
            if (isPTZCamera && !ptzRegion.current) {
                ptzRegion.current = new PTZRegion(
                    canvasRef.current,
                    videoElement,
                    divRef.current,
                    digitalZoomRef.current,
                    camId,
                    setPTZRequestStatus
                );
                // PTZ may already be toggled on (e.g. restored from persisted state on remount),
                // in which case the isPTZOn effect already ran before this region existed.
                if (isPTZOn) {
                    startNewRegion();
                }
            }
        },
        [isPTZCamera, camId, isPTZOn, startNewRegion]
    );

    useEffect(() => {
        if (isPTZOn && !isWindowDisconnectedFromSharedWorker) {
            startNewRegion();
        } else {
            if (ptzRegion.current) {
                ptzRegion.current.close();
                setCanvasVisible(false);
            }
        }

        return () => {
            window.removeEventListener('keydown', keyDownListener);
        };
    }, [isPTZOn, isWindowDisconnectedFromSharedWorker, keyDownListener, startNewRegion]);

    useEffect(() => {
        return () => {
            ptzRegion.current?.close?.();
            ptzRegion.current = null;
        };
    }, []);

    useEffect(() => {
        onPTZOnChange?.(isPTZOn);
    }, [isPTZOn, onPTZOnChange]);

    const fullZoomIn = useCallback(() => ptzRegion.current?.fullZoomIn(), []);
    const fullZoomOut = useCallback(() => ptzRegion.current?.fullZoomOut(), []);

    return {
        togglePTZActive,
        isPTZCamera,
        isPTZOn,
        canvasRef,
        ptzRegion,
        canvasInlineStyles,
        initializeNewPTZRegion,
        togglePTZCameraEnlarged,
        ptzRequestStatus,
        fullZoomIn,
        fullZoomOut,
    };
}
